import { useState, useEffect, useCallback, useMemo } from "react";
import { DataTable, ColumnDef } from "@/components/DataTable";
import { StatusBadge } from "@/components/ui/status-badge";
import { EscalationTypeBadge } from "@/components/ui/escalation-type-badge";
import { FilterBar } from "@/components/ui/filter-bar";
import { SplitLayout } from "@/components/SplitLayout";
import { ReviewNoticePanel } from "@/components/ReviewNoticePanel";
import { api, ApiClientError, ReviewNotice } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export function ReviewNotices() {
  const { logout } = useAuth();
  const [notices, setNotices] = useState<ReviewNotice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedNotice, setSelectedNotice] = useState<ReviewNotice | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState("all");
  const [escalationFilter, setEscalationFilter] = useState("all");
  const [sortBy, setSortBy] = useState("schedule");

  const loadNotices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.reviewNotices.list();
      setNotices(data);
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 401) {
          logout();
        } else {
          const errorMsg = err.detail || `Failed to load review notices (${err.status})`;
          console.error("Review notices error:", err);
          setError(errorMsg);
        }
      } else {
        console.error("Review notices error:", err);
        setError(`Failed to load review notices: ${err instanceof Error ? err.message : String(err)}`);
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    loadNotices();
  }, [loadNotices]);

  const handleApproveNotice = async () => {
    if (!selectedNotice) return;
    
    setApproving(true);
    try {
      await api.reviewNotices.approve(selectedNotice.escalation_step_id);
      
      // Remove approved notice from list
      setNotices(notices.filter(n => n.id !== selectedNotice.id));
      setSelectedNotice(null);
      setIsPanelOpen(false);
      
      // Optionally reload to get fresh data
      await loadNotices();
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 401) {
          logout();
        } else {
          setError(err.detail || "Failed to approve notice");
        }
      } else {
        setError("Failed to approve notice");
      }
    } finally {
      setApproving(false);
    }
  };

  // Helper functions for table display
  const getStatusVariant = (status: string): "pending" | "approved" | "sent" => {
    switch (status.toLowerCase()) {
      case "pending":
        return "pending";
      case "in_progress":
        return "approved";
      case "completed":
        return "sent";
      default:
        return "pending";
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status.toLowerCase()) {
      case "pending":
        return "Pending Review";
      case "in_progress":
        return "Admin Approved";
      case "completed":
        return "Sent";
      default:
        return status;
    }
  };

  const formatSendTime = (sendTime: string | null) => {
    if (!sendTime || sendTime === "Immediate") return "Immediate";
    try {
      const date = new Date(sendTime);
      return date.toLocaleString();
    } catch {
      return sendTime;
    }
  };

  // Apply filters to data
  const filteredNotices = useMemo(() => {
    return notices.filter(notice => {
      const matchesStatus = statusFilter === "all" || notice.status.toLowerCase() === statusFilter.toLowerCase();
      const matchesEscalation = escalationFilter === "all" || notice.escalation_type.toLowerCase().includes(escalationFilter.toLowerCase());
      return matchesStatus && matchesEscalation;
    });
  }, [notices, statusFilter, escalationFilter]);

  // Sort data
  const sortedNotices = useMemo(() => {
    const sorted = [...filteredNotices];
    switch (sortBy) {
      case "schedule":
        return sorted.sort((a, b) => {
          const aTime = a.send_time || "0";
          const bTime = b.send_time || "0";
          return aTime.localeCompare(bTime);
        });
      case "case":
        return sorted.sort((a, b) => (a.case_number || a.case_id).localeCompare(b.case_number || b.case_id));
      case "agency":
        return sorted.sort((a, b) => a.agency_name.localeCompare(b.agency_name));
      case "status":
        return sorted.sort((a, b) => a.status.localeCompare(b.status));
      default:
        return sorted;
    }
  }, [filteredNotices, sortBy]);

  // Define columns for the Review Notices table
  const columns: ColumnDef<ReviewNotice>[] = [
    {
      key: "subject_line",
      header: "Subject Line",
      render: (notice) => (
        <div
          className="text-sm text-foreground cursor-pointer hover:text-primary"
          onClick={() => {
            setSelectedNotice(notice);
            setIsPanelOpen(true);
          }}
        >
          {notice.subject_line}
        </div>
      ),
      sortValue: (notice) => notice.subject_line,
    },
    {
      key: "status",
      header: "Status",
      render: (notice) => (
        <StatusBadge status={getStatusVariant(notice.status)}>
          {getStatusLabel(notice.status)}
        </StatusBadge>
      ),
      sortValue: (notice) => notice.status,
    },
    {
      key: "send_time",
      header: "Send Time",
      render: (notice) => (
        <span className={notice.send_time ? 'text-foreground text-sm' : 'text-orange-600 text-sm font-medium'}>
          {formatSendTime(notice.send_time)}
        </span>
      ),
      sortValue: (notice) => notice.send_time || "",
    },
    {
      key: "agent_name",
      header: "User",
      render: (notice) => (
        <div className="text-sm text-foreground">{notice.agent_name}</div>
      ),
      sortValue: (notice) => notice.agent_name,
    },
    {
      key: "case_number",
      header: "Case",
      render: (notice) => (
        <div className="text-sm text-blue-600">
          {notice.case_number || notice.case_id.slice(0, 8)}
        </div>
      ),
      sortValue: (notice) => notice.case_number || notice.case_id,
    },
    {
      key: "agency_name",
      header: "Agency",
      render: (notice) => (
        <div className="text-sm text-foreground">{notice.agency_name}</div>
      ),
      sortValue: (notice) => notice.agency_name,
    },
    {
      key: "recipient_email",
      header: "Recipient Email",
      render: (notice) => (
        <div className="text-sm text-foreground">{notice.recipient_email}</div>
      ),
      sortValue: (notice) => notice.recipient_email,
    },
    {
      key: "escalation_type",
      header: "Escalation",
      render: (notice) => <EscalationTypeBadge type={notice.escalation_type} />,
      sortValue: (notice) => notice.escalation_type,
    },
  ];

  const handleNoticeUpdate = (updatedNotice: ReviewNotice) => {
    setNotices(notices.map(n => n.id === updatedNotice.id ? updatedNotice : n));
    setSelectedNotice(updatedNotice);
  };

  const panelContent = selectedNotice && (
    <ReviewNoticePanel
      notice={selectedNotice}
      onApprove={handleApproveNotice}
      approving={approving}
      onUpdate={handleNoticeUpdate}
    />
  );

  const filterBarComponent = (
    <FilterBar
      filters={[
        {
          value: statusFilter,
          onValueChange: setStatusFilter,
          options: [
            { value: "all", label: "All Statuses" },
            { value: "pending", label: "Pending Review" },
            { value: "in_progress", label: "Admin Approved" },
            { value: "completed", label: "Sent" },
          ],
          placeholder: "All Statuses",
          searchPlaceholder: "Search status...",
        },
        {
          value: escalationFilter,
          onValueChange: setEscalationFilter,
          options: [
            { value: "all", label: "All Escalations" },
            { value: "hosting_provider", label: "Hosting Provider" },
            { value: "registrar", label: "Registrar" },
            { value: "webmaster", label: "Webmaster" },
            { value: "cdn_provider", label: "CDN Provider" },
            { value: "dns_provider", label: "DNS Provider" },
          ],
          placeholder: "All Escalations",
          searchPlaceholder: "Search escalation type...",
        },
        {
          value: sortBy,
          onValueChange: setSortBy,
          options: [
            { value: "schedule", label: "Sort by Schedule" },
            { value: "case", label: "Sort by Case" },
            { value: "agency", label: "Sort by Agency" },
            { value: "status", label: "Sort by Status" },
          ],
          placeholder: "Sort by Schedule",
          searchPlaceholder: "Search sort option...",
        },
      ]}
    />
  );

  return (
    <SplitLayout
      isPanelOpen={isPanelOpen}
      onPanelClose={() => {
        setIsPanelOpen(false);
        setSelectedNotice(null);
      }}
      panelTitle="Notice Review"
      panel={panelContent}
    >
      <DataTable
        title="Review Notices"
        subtitle="Review and approve takedown notices before they are sent"
        columns={columns}
        data={sortedNotices}
        loading={loading}
        error={error}
        searchPlaceholder="Search notices..."
        entityName="notices"
        selectedId={selectedNotice?.id || null}
        filterBar={filterBarComponent}
      />
    </SplitLayout>
  );
}
